import axios from 'axios';
import { getApiUrl } from '@/config/api';
import authService from '@/shared/services/auth';

class CatMonedaService {
  constructor() {
    this.monedas = [];
    this.loading = false;
    this.error = null;
    this.selectedEmpresa = null;
  }

  async initialize() {
    // Esperar un poco para que la empresa se seleccione
    await this.waitForEmpresa();
    await this.listar();
  }

  // Esperar a que la empresa esté seleccionada
  async waitForEmpresa() {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo
    
    while (attempts < maxAttempts) {
      this.selectedEmpresa = authService.getSelectedEmpresa();
      if (this.selectedEmpresa && this.selectedEmpresa.EmpresaId) {
        console.log('Empresa encontrada para CatMoneda:', this.selectedEmpresa);
        return;
      }
      
      // Esperar 100ms antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    // Si no se encuentra empresa después de los intentos
    this.error = 'No se pudo obtener la empresa seleccionada';
    console.error('No se pudo obtener empresa para CatMoneda después de', maxAttempts, 'intentos');
  }

  async listar() {
    this.loading = true;
    this.error = null;
    
    try {
      // Verificar que tenemos empresa seleccionada
      if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
        this.selectedEmpresa = authService.getSelectedEmpresa();
        if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
          this.loading = false;
          return { success: false, data: [], message: 'Debe elegir una empresa antes de poder continuar.' };
        }
      }

      const token = authService.getToken();
      const response = await axios.post(getApiUrl('/api/CatMoneda/listar'), {
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      
      // Filtrar por empresa en el frontend como medida de seguridad adicional
      const allMonedas = response.data || [];
      this.monedas = allMonedas.filter(moneda => moneda.EmpresaId === this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('Monedas cargados:', this.monedas.length, 'de', allMonedas.length, 'total');
      console.log('Empresa del usuario:', this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('Monedas cargadas:', this.monedas.length);
      
      return {
        success: true,
        data: this.monedas,
        message: 'Monedas cargadas exitosamente'
      };
    } catch (error) {
      console.error('Error al cargar Monedas:', error);
      this.error = error.message;
      this.monedas = [];
      this.loading = false;
      throw error;
    }
  }

  async guardar(monedaData) {
    try {
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const dataToSend = {
        ...monedaData,
        EmpresaId: selectedEmpresa?.EmpresaId || monedaData.EmpresaId
      };

      const response = await axios.post(getApiUrl('/api/CatMoneda/guardar'), dataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return {
        success: true,
        data: response.data,
        message: 'Moneda guardada exitosamente'
      };
    } catch (error) {
      console.error('Error al guardar Moneda:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al guardar Moneda'
      };
    }
  }

  async borrar(monedaData) {
    try {
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const dataToSend = {
        ...monedaData,
        EmpresaId: selectedEmpresa?.EmpresaId || monedaData.EmpresaId
      };

      const response = await axios.post(getApiUrl('/api/CatMoneda/borrar'), dataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return {
        success: true,
        data: response.data,
        message: 'Moneda eliminada exitosamente'
      };
    } catch (error) {
      console.error('Error al eliminar Moneda:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al eliminar Moneda'
      };
    }
  }

  async lectura(clave) {
    try {
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const response = await axios.post(getApiUrl('/api/CatMoneda/lectura'), {
        Clave: clave,
        EmpresaId: selectedEmpresa?.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error al leer Moneda:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al leer Moneda'
      };
    }
  }

  async recargar() {
    await this.listar();
  }

  async downloadBulk() {
    try {
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const response = await axios.post(getApiUrl('/api/CatMoneda/descarga-masiva'), {
        EmpresaId: selectedEmpresa?.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });

      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monedas_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: 'Descarga iniciada correctamente'
      };
    } catch (error) {
      console.error('Error al descargar Monedas:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al descargar Monedas'
      };
    }
  }
}

export default CatMonedaService;